// Em produção as chamadas vão para /api/... (mesmo origin, Express faz proxy para Flask).
// Em dev com react-scripts start o CRA redireciona via "proxy" no package.json.
// Pode ser sobrescrito com REACT_APP_API_URL para apontar para outra instância.
const API_URL = process.env.REACT_APP_API_URL ?? "";

async function parseResponse(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || "Nao foi possivel concluir a solicitacao.");
    }
    return data;
}

export async function createLead(payload) {
    const response = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    return parseResponse(response);
}

export async function validateCrmPassword(password) {
    const response = await fetch(`${API_URL}/api/auth`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
    });
    return parseResponse(response);
}

export async function fetchLeads(password, filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
    });

    const qs = params.toString();
    const response = await fetch(`${API_URL}/api/leads${qs ? `?${qs}` : ""}`, {
        headers: {
            "X-CRM-Password": password,
        },
    });
    return parseResponse(response);
}

export async function updateLead(password, id, payload) {
    const response = await fetch(`${API_URL}/api/leads/${id}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            "X-CRM-Password": password,
        },
        body: JSON.stringify(payload),
    });
    return parseResponse(response);
}

// ─── Blog – público ────────────────────────────────────────────────────────────

export async function fetchPublishedPosts(params = {}) {
    const qs = new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
    ).toString();
    const response = await fetch(`${API_URL}/api/posts${qs ? `?${qs}` : ""}`);
    return parseResponse(response);
}

export async function fetchPublishedPost(slug) {
    const response = await fetch(`${API_URL}/api/posts/${slug}`);
    return parseResponse(response);
}

// ─── Tracking de acessos ────────────────────────────────────────────────────────

export async function trackPageView({ path, postSlug, referrer, consent, consentLevel }) {
    // credentials: include é necessário para o cookie hl_vid (visitante recorrente).
    const response = await fetch(`${API_URL}/api/track`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            path,
            post_slug: postSlug ?? null,
            referrer: referrer ?? "",
            consent: Boolean(consent),
            consent_level: consentLevel ?? (consent ? "all" : "none"),
        }),
    });
    return parseResponse(response);
}

export async function fetchAnalytics(password, range = "30d") {
    const response = await fetch(`${API_URL}/api/admin/analytics?range=${encodeURIComponent(range)}`, {
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

// ─── Blog – admin ──────────────────────────────────────────────────────────────

export async function fetchAdminPosts(password) {
    const response = await fetch(`${API_URL}/api/admin/posts`, {
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

export async function fetchAdminPost(password, id) {
    const response = await fetch(`${API_URL}/api/admin/posts/${id}`, {
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

export async function createPost(password, payload) {
    const response = await fetch(`${API_URL}/api/admin/posts`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-CRM-Password": password,
        },
        body: JSON.stringify(payload),
    });
    return parseResponse(response);
}

export async function updatePost(password, id, payload) {
    const response = await fetch(`${API_URL}/api/admin/posts/${id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            "X-CRM-Password": password,
        },
        body: JSON.stringify(payload),
    });
    return parseResponse(response);
}

export async function deletePost(password, id) {
    const response = await fetch(`${API_URL}/api/admin/posts/${id}`, {
        method: "DELETE",
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

export async function uploadImage(password, file) {
    const formData = new FormData();
    formData.append("file", file);

    // Sem "Content-Type": o browser define o boundary do multipart automaticamente.
    const response = await fetch(`${API_URL}/api/admin/upload`, {
        method: "POST",
        headers: { "X-CRM-Password": password },
        body: formData,
    });
    return parseResponse(response);
}

// ─── Integração Google Drive (OAuth) ───────────────────────────────────────────

export async function fetchDriveStatus(password) {
    const response = await fetch(`${API_URL}/api/admin/drive/status`, {
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

export async function connectDrive(password) {
    const response = await fetch(`${API_URL}/api/admin/drive/connect`, {
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

export async function disconnectDrive(password) {
    const response = await fetch(`${API_URL}/api/admin/drive/disconnect`, {
        method: "POST",
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

// ─── Comments – público ────────────────────────────────────────────────────────

export async function fetchPostComments(slug) {
    const response = await fetch(`${API_URL}/api/posts/${slug}/comments`);
    return parseResponse(response);
}

export async function createComment(slug, idToken, content) {
    const response = await fetch(`${API_URL}/api/posts/${slug}/comments`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ content }),
    });
    return parseResponse(response);
}

// ─── Curtidas (likes) – público ─────────────────────────────────────────────────

export async function fetchPostLikes(slug, visitorId) {
    const qs = visitorId ? `?visitor_id=${encodeURIComponent(visitorId)}` : "";
    const response = await fetch(`${API_URL}/api/posts/${slug}/likes${qs}`);
    return parseResponse(response);
}

export async function togglePostLike(slug, visitorId) {
    const response = await fetch(`${API_URL}/api/posts/${slug}/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_id: visitorId }),
    });
    return parseResponse(response);
}

// ─── Comments – admin ───────────────────────────────────────────────────────────

export async function fetchAdminComments(password) {
    const response = await fetch(`${API_URL}/api/admin/comments`, {
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

export async function updateCommentStatus(password, id, status) {
    const response = await fetch(`${API_URL}/api/admin/comments/${id}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            "X-CRM-Password": password,
        },
        body: JSON.stringify({ status }),
    });
    return parseResponse(response);
}

export async function deleteComment(password, id) {
    const response = await fetch(`${API_URL}/api/admin/comments/${id}`, {
        method: "DELETE",
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}

// ─── Agentes ──────────────────────────────────────────────────────────────────

export async function fetchAgents(password) {
    const response = await fetch(`${API_URL}/api/admin/agents`, {
        headers: { "X-CRM-Password": password },
    });
    return parseResponse(response);
}
